import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, Trash2, PenLine, Info, Archive, ArchiveRestore } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  DeleteConfirmDialog,
  VariantDialog,
  SaladDialog,
  SauceDialog,
  AllergenDialog,
  DetailsDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  deleteKebabById,
  clearKebabMessages,
  updateExistingKebab,
  fetchAllKebabs,
} from "@/redux/slice/menu-manage-slice/kebab-slice";
import hasPermission from "@/hooks/hasPermission";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function KababListTable({ kebabs }) {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const { deleteLoading, updateLoading, requestStatus, error } = useSelector(
    (state) => state.menuManage.kebab
  );
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [itemDialog, setItemDialog] = useState(null);
  const [sizeAndPriceDialog, setSizeAndPriceDialog] = useState(false);
  const [saladDialog, setSaladDialog] = useState(false);
  const [souceDialog, setSouce] = useState(false);
  const [allergenDialog, setAllergenDialog] = useState(false);
  const [vegetarianDetailsDialog, setVegetarianDetailsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
    setItemDialog(null);
  };
  const closeSizeAndPrice = () => {
    setSizeAndPriceDialog(false);
    setItemDialog(null);
  };
  const closeSalad = () => {
    setSaladDialog(false);
    setItemDialog(null);
  };
  const closeSouceDialog = () => {
    setSouce(false);
    setItemDialog(null);
  };
  const closeAllergen = () => {
    setAllergenDialog(false);
    setItemDialog(null);
  };
  const closeVegetarianDetails = () => {
    setVegetarianDetailsDialog(false);
    setItemDialog(null);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearKebabMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const res = await dispatch(
        updateExistingKebab({
          id,
          data: {
            ...item,
            status: newStatus,
          },
        })
      );
      if (updateExistingKebab.fulfilled.match(res)) {
        toast.success(`Kebab archived successfully!`);
      } else if (updateExistingKebab.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive kebab.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearKebabMessages());
      const res = await dispatch(deleteKebabById(id));
      if (deleteKebabById.fulfilled.match(res)) {
        toast.success(`Kebab deleted successfully!`);
      } else if (deleteKebabById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete kebab.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("kebabs", id);
      if (res.status === "success") {
        toast.success(res.message || "Kebab status updated successfully");
        dispatch(fetchAllKebabs(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <Table className="w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px]">Item No</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Size & Price</TableHead>
          <TableHead>Salad</TableHead>
          <TableHead>Sauce</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {kebabs && kebabs.length > 0 ? (
          kebabs?.map((item, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">
                {item?.item_no || "N/A"}
              </TableCell>
              <TableCell>{item?.name}</TableCell>
              <TableCell>
                <span className="flex gap-1 cursor-pointer items-center">
                  {item?.variants_count}
                  <Eye
                    onClick={() => {
                      setSizeAndPriceDialog(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <span className="flex cursor-pointer items-center gap-1">
                  {item?.salads_count}
                  <Eye
                    onClick={() => {
                      setSaladDialog(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center cursor-pointer">
                  {item?.sauces_count}
                  <Eye
                    onClick={() => {
                      setSouce(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {item?.status === "active" ? (
                    <>
                      <span className="text-cgreen text-base">Active</span>
                      <Switch
                        checked={true}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "inactive" ? (
                    <>
                      <span className="text-cred text-base">Inactive</span>
                      <Switch
                        checked={false}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "archived" ? (
                    <span className="text-cred text-base">Archived</span>
                  ) : (
                    <span className="text-cred text-base">Draft</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right justify-end flex gap-2 items-center">
                <div className="flex items-center gap-2 h-8">
                  <Info
                    onClick={() => {
                      setVegetarianDetailsDialog(true);
                      setItemDialog(item);
                    }}
                    className="text-cwhite3 cursor-pointer"
                    size={18}
                  />

                  {hasPermission("back_office.menu_management.update_kebab") && item?.status !== "archived" && (
                    <Link
                      to={"/backoffice/menu/kebab/update"}
                      state={item}
                      className="cursor-pointer"
                    >
                      <PenLine className="text-cyellow" size={18} />
                    </Link>
                  )}

                  {hasPermission("back_office.menu_management.delete_kebab") && item?.status !== "archived" && (
                     <span>
                       {item?.status === "draft" ? (
                         <Trash2
                           onClick={() => {
                             setItemDialog(item);
                             setDeleteDialog(true);
                           }}
                           className="text-cred cursor-pointer"
                           size={18}
                         />
                       ) : (
                         <Archive
                           onClick={() => {
                             setArchiveActionType("archive");
                             setItemDialog(item);
                             setArchiveDialog(true);
                           }}
                           className="text-cred cursor-pointer"
                           size={18}
                         />
                       )}
                     </span>
                   )}

                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={7} className="text-center">
              No data available
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      {/* dialog */}
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemDialog.id, itemDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Kebab?" : "Restore Kebab?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemDialog?.name}"?`
            : `Are you sure you want to restore "${itemDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemDialog.id);
        }}
        title="Delete Draft Kebab?"
        description={`Are you sure you want to delete the draft kebab "${itemDialog?.name}"? This action cannot be undone.`}
      />
      <VariantDialog
        teaCoffee={true}
        items={itemDialog}
        isOpen={sizeAndPriceDialog}
        onClose={closeSizeAndPrice}
      />
      <SaladDialog
        items={itemDialog}
        isOpen={saladDialog}
        onClose={closeSalad}
      />
      <SauceDialog
        items={itemDialog}
        isOpen={souceDialog}
        onClose={closeSouceDialog}
      />
      <AllergenDialog
        items={itemDialog}
        isOpen={allergenDialog}
        onClose={closeAllergen}
      />
      <DetailsDialog
        items={itemDialog}
        isOpen={vegetarianDetailsDialog}
        onClose={closeVegetarianDetails}
      />
    </Table>
  );
}

export default KababListTable;
